/**
 * @tensilelabs/auth-client — WebAuthn Passkey Ceremonies (`rpId: "tensilelabs.com"`)
 *
 * Two rules shape this module:
 *
 * 1. **Never fake a ceremony silently.** A browser that has WebAuthn must produce a real prompt or
 *    a real error. Swallowing the failure and returning a deterministic software passkey is what
 *    made "create passkey" look like a no-op: no prompt appeared, a synthetic credential was
 *    registered, and the reason never reached the page.
 * 2. **The PRF output is only trustworthy from `navigator.credentials.get()`.** Chrome (and CTAP
 *    authenticators without `hmac-secret-mc`) report only `prf.enabled` at registration, so the
 *    registration ceremony is followed by one immediate assertion with the same salt and the same
 *    `userVerification` policy. Yubico's PRF guide documents exactly this "create() then get()"
 *    flow; the derived key must also be reproducible at login, which only holds if both ceremonies
 *    use the same salt and the same UV policy.
 *
 * The deterministic software passkey survives for one purpose only: environments with no
 * authenticator at all (jsdom, Playwright, `wrangler dev`). It is used only when the browser
 * cannot run a ceremony — never as a fallback for a ceremony that failed.
 */

import { type Bytes, base64ToBytes, bytesToBase64 } from "./crypto.ts";

export interface PasskeyCeremonyResult {
  credentialIdB64: string;
  prfOutput: Bytes;
  prfSalt: Bytes;
  rpIdUsed: string;
}

/** True when this environment can run a real WebAuthn ceremony. */
export function isWebAuthnAvailable(): boolean {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;
  return Boolean(window.PublicKeyCredential && navigator.credentials?.create);
}

/**
 * Resolves the effective WebAuthn `rpId` for the current browser hostname.
 * Uses `"tensilelabs.com"` on any `*.tensilelabs.com` subdomain so a single Passkey
 * works across Task Jinn and all future Tensile Labs tools, and falls back to the
 * current hostname in local development (`localhost`).
 */
export function resolveEffectiveRpId(preferredRpId = "tensilelabs.com"): string {
  if (typeof window === "undefined") {
    return preferredRpId;
  }
  const host = window.location.hostname;
  if (host === preferredRpId || host.endsWith(`.${preferredRpId}`)) {
    return preferredRpId;
  }
  return host;
}

/**
 * Turns a WebAuthn rejection into a message the page can show.
 *
 * `NotAllowedError` is the browser's catch-all for "the user dismissed it, it timed out, or the
 * RP/origin did not match", so it gets the phrasing that actually helps.
 */
function describeCeremonyFailure(cause: unknown, action: "create" | "assert"): Error {
  const name = cause instanceof DOMException ? cause.name : "";
  const detail = cause instanceof Error ? cause.message : String(cause);
  if (name === "NotAllowedError" || /not allowed|timed out|dismiss/i.test(detail)) {
    return new Error(
      `Passkey ${action === "create" ? "creation" : "verification"} was cancelled or timed out. ` +
        "Approve the browser prompt (and allow the site to use passkeys) to continue.",
    );
  }
  if (name === "NotSupportedError") {
    return new Error(
      `This authenticator does not support the requested passkey algorithm or the PRF extension (${detail}).`,
    );
  }
  if (name === "SecurityError") {
    return new Error(
      `The browser refused the passkey ceremony for this origin (${detail}). ` +
        "WebAuthn needs https:// (or localhost) and an rpId that is the current domain or its parent.",
    );
  }
  if (name === "InvalidStateError") {
    return new Error("This passkey is already registered on this authenticator. Try signing in instead.");
  }
  return new Error(`Passkey ${action} failed: ${detail}`);
}

/** Reads `prf.results.first` from a ceremony response, when the authenticator produced one. */
function readPrfFirst(credential: PublicKeyCredential): Bytes | null {
  const results = credential.getClientExtensionResults() as {
    prf?: { enabled?: boolean; results?: { first?: ArrayBuffer } };
  };
  const first = results?.prf?.results?.first;
  if (first && first.byteLength >= 16) {
    return new Uint8Array(first);
  }
  return null;
}

/**
 * Registers a new WebAuthn Passkey with the PRF extension enabled and returns its PRF output.
 *
 * The create() call primes the credential (some authenticators with `hmac-secret-mc` already return
 * `prf.results`, which is used when present); otherwise one immediate assertion derives the same
 * value the login ceremony will derive.
 */
export async function registerPasskeyWithPrf(params: {
  email: string;
  userId: string;
  challengeB64: string;
  rpId?: string;
}): Promise<PasskeyCeremonyResult> {
  const rpId = resolveEffectiveRpId(params.rpId);
  const prfSalt = crypto.getRandomValues(new Uint8Array(32));

  if (isWebAuthnAvailable()) {
    const challenge = base64ToBytes(params.challengeB64);
    const userIdBytes = new TextEncoder().encode(params.userId).slice(0, 32);

    let cred: PublicKeyCredential | null;
    try {
      cred = (await navigator.credentials.create({
        publicKey: {
          rp: {
            id: rpId,
            name: "Tensile Labs (Task Jinn)",
          },
          user: {
            id: userIdBytes,
            name: params.email,
            displayName: params.email.split("@")[0],
          },
          challenge,
          pubKeyCredParams: [
            { type: "public-key", alg: -8 }, // Ed25519
            { type: "public-key", alg: -7 }, // ES256
            { type: "public-key", alg: -257 }, // RS256
          ],
          authenticatorSelection: {
            residentKey: "required",
            userVerification: "required",
          },
          timeout: 60000,
          extensions: {
            // `eval` primes the credential's PRF key material at registration. Browsers may not
            // evaluate it here; the assertion below covers that case.
            prf: {
              eval: {
                first: prfSalt,
              },
            },
          },
        },
      })) as PublicKeyCredential | null;
    } catch (cause) {
      throw describeCeremonyFailure(cause, "create");
    }

    if (!cred) {
      throw new Error("The authenticator returned no passkey; the creation prompt was dismissed.");
    }

    const credentialIdB64 = bytesToBase64(new Uint8Array(cred.rawId));
    const fromCreate = readPrfFirst(cred);
    if (fromCreate) {
      return { credentialIdB64, prfOutput: fromCreate, prfSalt, rpIdUsed: rpId };
    }

    const derived = await assertPrfOutput({
      credentialIdB64,
      prfSalt,
      rpId,
      challenge: crypto.getRandomValues(new Uint8Array(32)),
    });
    return {
      credentialIdB64,
      // A passkey without PRF cannot wrap a master key. Fail loudly instead of sealing the vault
      // under a value that no future ceremony can reproduce.
      prfOutput: derived?.prfOutput ?? failNoPrf(),
      prfSalt,
      rpIdUsed: rpId,
    };
  }

  return softwarePasskey(params.email, rpId, prfSalt);
}

/** Asserts an existing WebAuthn Passkey and evaluates the PRF extension with the stored `prfSaltB64`. */
export async function assertPasskeyWithPrf(params: {
  email: string;
  challengeB64: string;
  prfSaltB64: string;
  credentialIdB64?: string;
  rpId?: string;
}): Promise<PasskeyCeremonyResult> {
  const rpId = resolveEffectiveRpId(params.rpId);
  const prfSalt = base64ToBytes(params.prfSaltB64);

  if (isWebAuthnAvailable()) {
    const challenge = base64ToBytes(params.challengeB64);
    const derived = await assertPrfOutput({
      credentialIdB64: params.credentialIdB64,
      prfSalt,
      rpId,
      challenge,
    });
    const credentialIdB64 =
      derived?.credentialIdB64 ??
      params.credentialIdB64 ??
      (await softwareCredentialId(params.email));
    return {
      // Prefer the id the authenticator actually used: a synced or roaming credential may differ
      // from the one the envelope names, and the wrapped seed is bound to the real credential.
      credentialIdB64,
      prfOutput: derived?.prfOutput ?? failNoPrf(),
      prfSalt,
      rpIdUsed: rpId,
    };
  }

  return softwarePasskey(params.email, rpId, prfSalt, params.credentialIdB64);
}

function failNoPrf(): never {
  throw new Error(
    "This passkey did not return a PRF output, so it cannot unlock the vault. " +
      "Use a platform passkey (iCloud Keychain, Google Password Manager) or a security key with " +
      "hmac-secret support, and make sure the PRF extension is allowed in this browser.",
  );
}

/**
 * One assertion that only asks for the PRF output.
 *
 * `userVerification` must match the policy used at registration: CTAP derives the PRF from
 * `CredRandomWithUV` or `CredRandomWithoutUV` depending on it, so a mismatch silently yields a
 * different key.
 */
async function assertPrfOutput(params: {
  credentialIdB64?: string;
  prfSalt: Bytes;
  rpId: string;
  challenge: Bytes;
}): Promise<{ credentialIdB64: string; prfOutput: Bytes } | null> {
  const allowCredentials: PublicKeyCredentialDescriptor[] = params.credentialIdB64
    ? [{ type: "public-key", id: base64ToBytes(params.credentialIdB64) }]
    : [];

  let assertion: PublicKeyCredential | null;
  try {
    assertion = (await navigator.credentials.get({
      publicKey: {
        rpId: params.rpId,
        challenge: params.challenge,
        allowCredentials,
        userVerification: "required",
        timeout: 60000,
        extensions: {
          prf: {
            eval: {
              first: params.prfSalt,
            },
          },
        },
      },
    })) as PublicKeyCredential | null;
  } catch (cause) {
    throw describeCeremonyFailure(cause, "assert");
  }

  if (!assertion) return null;
  const prfOutput = readPrfFirst(assertion);
  if (!prfOutput) return null;
  return { credentialIdB64: bytesToBase64(new Uint8Array(assertion.rawId)), prfOutput };
}

/**
 * The pre-2026-11 registration key: `SHA-256(rawId || prfSalt)`.
 *
 * Browsers do not evaluate `prf.eval` during create(), so registration used to seal the Master
 * Identity seed under a value derived from the credential id and the salt — both of which are
 * stored in the device envelope. That made the wrapping key public material (confidentiality of the
 * vault was effectively nil) and disagreed with the assertion ceremony, which does return a real
 * PRF output. New registrations use the real PRF output; this helper exists so envelopes written
 * before that fix can still be opened exactly once, at which point the caller re-wraps them.
 */
export async function legacyDigestPrfOutput(params: {
  credentialIdB64: string;
  prfSaltB64: string;
}): Promise<Bytes> {
  const credentialId = base64ToBytes(params.credentialIdB64);
  const prfSalt = base64ToBytes(params.prfSaltB64);
  const material = new Uint8Array(credentialId.length + prfSalt.length);
  material.set(credentialId, 0);
  material.set(prfSalt, credentialId.length);
  return new Uint8Array(await crypto.subtle.digest("SHA-256", material));
}

/**
 * Deterministic software passkey for environments with no authenticator (jsdom, Playwright,
 * `wrangler dev`). Reachable only when `isWebAuthnAvailable()` is false — a failed ceremony never
 * lands here, because a silent downgrade is indistinguishable from a working passkey.
 */
async function softwarePasskey(
  email: string,
  rpId: string,
  prfSalt: Bytes,
  credentialIdB64?: string,
): Promise<PasskeyCeremonyResult> {
  console.warn(
    "[tensile-auth] WebAuthn is unavailable in this environment; using the deterministic " +
      "software passkey. This must never happen in a real browser.",
  );
  const rawMaterial = new TextEncoder().encode(
    `tensilelabs:soft-passkey:${rpId}:${email.toLowerCase()}`,
  );
  const digest = await crypto.subtle.digest("SHA-256", rawMaterial);
  return {
    credentialIdB64: credentialIdB64 ?? (await softwareCredentialId(email)),
    prfOutput: new Uint8Array(digest),
    prfSalt,
    rpIdUsed: rpId,
  };
}

async function softwareCredentialId(email: string): Promise<string> {
  const credDigest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(`cred:${email.toLowerCase()}`),
  );
  return bytesToBase64(new Uint8Array(credDigest).slice(0, 24));
}
